import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, RefreshCw, UserCheck, Clock, CheckCircle2, XCircle,
  Stethoscope, X, Search, Trash2, Save, AlertCircle, User, Printer, Activity, Eye, FileText,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { api, opdApi, patientApi } from '@/lib/api';
import PatientQuickViewModal from '@/features/patients/components/PatientQuickViewModal';
import ListSearchInput from '@/components/ui/ListSearchInput';
import { useBranding } from '@/hooks/useBranding';
import { usePatientSearch } from '@/hooks/usePatientSearch';
import { clsx } from 'clsx';
import { useAuthStore } from '@/store/auth.store';
import BlankRxPrint, { BlankRxData } from '@/components/prescription/BlankRxPrint';
import DigitalPrescriptionPrint, { DigitalRxData } from '@/shared/components/prescription/DigitalPrescriptionPrint';

// ── Types ──────────────────────────────────────────────────────────

interface RxItem {
  drug_name:    string;
  drug_id?:     string;
  dose:         string;
  frequency:    string;
  duration:     string;
  quantity?:    number;
  instructions?: string;
}

interface ConsultState {
  soap_subjective:    string;
  soap_objective:     string;
  soap_assessment:    string;
  soap_plan:          string;
  primary_icd10_code: string;
  primary_icd10_desc: string;
  follow_up_date:     string;
  referral_to:        string;
  rxItems:            RxItem[];
  general_instructions: string;
}

// ── Helpers ────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  waiting:     { label: 'Waiting',     color: 'bg-blue-50 text-blue-700 border-blue-200' },
  in_progress: { label: 'In Progress', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  completed:   { label: 'Completed',   color: 'bg-green-50 text-green-700 border-green-200' },
  cancelled:   { label: 'Cancelled',   color: 'bg-gray-100 text-gray-400 border-gray-200' },
  no_show:     { label: 'No Show',     color: 'bg-red-50 text-red-500 border-red-200' },
};

const FREQUENCY_OPTIONS = ['OD','BD','TDS','QID','SOS','Stat','5-7-9','Before meals','After meals','At bedtime'];
const DURATION_OPTIONS  = ['1 day','3 days','5 days','7 days','10 days','14 days','1 month','3 months','As directed'];

function StatCard({ label, value, icon: Icon, color }: any) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-card">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-gray-400 font-medium">{label}</span>
        <div className={clsx('w-8 h-8 rounded-lg flex items-center justify-center', color)}>
          <Icon size={16}/>
        </div>
      </div>
      <div className="text-3xl font-semibold text-navy">{value}</div>
    </div>
  );
}

// ── ICD-10 Search ──────────────────────────────────────────────────

function IcdSearch({ value, onChange }: { value: string; onChange: (code: string, desc: string) => void }) {
  const [q, setQ] = useState(value);
  const [open, setOpen] = useState(false);

  const { data: results = [] } = useQuery({
    queryKey: ['icd10', q],
    queryFn:  () => q.length >= 2
      ? api.get('/opd/icd10/search', { params: { q } }).then(r => r.data)
      : Promise.resolve([]),
    staleTime: Infinity,
  });

  return (
    <div className="relative">
      <input
        value={q}
        onChange={e => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Search ICD-10 code or description…"
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-navy"
      />
      {open && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-52 overflow-y-auto">
          {results.map((r: any) => (
            <button
              key={r.code}
              type="button"
              className="w-full text-left px-4 py-2.5 hover:bg-gray-50 border-b border-gray-50 last:border-0"
              onClick={() => {
                onChange(r.code, r.desc);
                setQ(`${r.code} — ${r.desc}`);
                setOpen(false);
              }}
            >
              <span className="font-mono text-xs text-blue-600 mr-2">{r.code}</span>
              <span className="text-sm text-gray-700">{r.desc}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Drug Search ────────────────────────────────────────────────────

function DrugSearch({ onSelect }: { onSelect: (drug: any) => void }) {
  const [q, setQ]       = useState('');
  const [open, setOpen] = useState(false);

  const { data: results = [] } = useQuery({
    queryKey: ['drug-search', q],
    queryFn:  () => q.length >= 2
      ? api.get('/drugs/search', { params: { q } }).then(r => r.data)
      : Promise.resolve([]),
    staleTime: 60_000,
  });

  return (
    <div className="relative">
      <div className="flex items-center gap-2 border border-dashed border-navy/30 rounded-lg px-3 py-2 hover:border-navy/60 transition-colors">
        <Search size={13} className="text-gray-400 flex-shrink-0"/>
        <input
          value={q}
          onChange={e => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search drug to add…"
          className="text-sm outline-none text-gray-700 placeholder:text-gray-300 flex-1"
        />
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
          {results.map((d: any) => (
            <button
              key={d.id}
              type="button"
              className="w-full text-left px-4 py-2.5 hover:bg-gray-50 border-b border-gray-50 last:border-0"
              onClick={() => {
                onSelect(d);
                setQ('');
                setOpen(false);
              }}
            >
              <div className="text-sm font-medium text-gray-800">{d.generic_name}</div>
              <div className="text-xs text-gray-400">{d.brand_name} · {d.formulation} {d.strength}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Previous Vitals Reference Card ────────────────────────────────
function PreviousVitalsCard({ patientId, excludeEncounterId }: { patientId: string; excludeEncounterId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['prev-vitals', patientId, excludeEncounterId],
    queryFn:  () => patientApi.prevVitals(patientId, excludeEncounterId).then(r => r.data),
    staleTime: 5 * 60_000,
  });

  const v = data?.vitals;
  if (isLoading || !v) return null;

  const hasAny = v.vitals_bp_systolic || v.vitals_pulse || v.vitals_spo2 || v.vitals_weight_kg;
  if (!hasAny) return null;

  return (
    <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
      <div className="text-2xs font-semibold text-blue-600 uppercase tracking-wide mb-2">
        Previous Visit Vitals
        {v.encounter_date && (
          <span className="normal-case font-normal ml-1.5 text-blue-400">
            ({format(parseISO(v.encounter_date), 'dd MMM yyyy')}
            {v.encounter_no ? ` · ${v.encounter_no}` : ''})
          </span>
        )}
      </div>
      <div className="flex gap-5 flex-wrap text-xs text-blue-800">
        {v.vitals_bp_systolic && (
          <span>BP: <strong>{v.vitals_bp_systolic}/{v.vitals_bp_diastolic}</strong> mmHg</span>
        )}
        {v.vitals_pulse && (
          <span>Pulse: <strong>{v.vitals_pulse}</strong> bpm</span>
        )}
        {v.vitals_temp_c && (
          <span>Temp: <strong>{v.vitals_temp_c}°C</strong></span>
        )}
        {v.vitals_spo2 && (
          <span>SpO₂: <strong>{v.vitals_spo2}%</strong></span>
        )}
        {v.vitals_weight_kg && (
          <span>Wt: <strong>{v.vitals_weight_kg} kg</strong></span>
        )}
        {v.vitals_height_cm && (
          <span>Ht: <strong>{v.vitals_height_cm} cm</strong></span>
        )}
      </div>
    </div>
  );
}

// ── Consultation Modal ─────────────────────────────────────────────

function ConsultModal({ encounter, onClose }: { encounter: any; onClose: () => void }) {
  const queryClient = useQueryClient();
  const user        = useAuthStore(s => s.user);
  const isDoctor    = user?.role === 'doctor' || user?.role === 'hospital_admin';
  const autoSaveRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [saving,    setSaving]    = useState(false);
  const [dirty,     setDirty]     = useState(false);
  const [saved,     setSaved]     = useState(false);
  const [saveError, setSaveError] = useState('');
  const [activeTab, setTab]       = useState<'soap' | 'rx'>('soap');

  const [state, setState] = useState<ConsultState>({
    soap_subjective:    encounter.soap_subjective    ?? '',
    soap_objective:     encounter.soap_objective     ?? '',
    soap_assessment:    encounter.soap_assessment    ?? '',
    soap_plan:          encounter.soap_plan          ?? '',
    primary_icd10_code: encounter.primary_icd10_code ?? '',
    primary_icd10_desc: encounter.primary_icd10_desc ?? '',
    follow_up_date:     encounter.follow_up_date     ?? '',
    referral_to:        encounter.referral_to        ?? '',
    rxItems:            [],
    general_instructions: '',
  });

  const set = (key: keyof ConsultState, val: any) => {
    setState(s => ({ ...s, [key]: val }));
    setDirty(true);
    setSaved(false);
  };

  // Mark in-progress on open
  const startMutation = useMutation({
    mutationFn: () => opdApi.updateEncounter(encounter.id, { status: 'in_progress' }),
  });

  useEffect(() => {
    if (encounter.status === 'waiting') startMutation.mutate();
    return () => { if (autoSaveRef.current) clearInterval(autoSaveRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-save every 30s
  const doSave = useCallback(async (markComplete = false): Promise<boolean> => {
    if (!dirty && !markComplete) return true;
    setSaving(true);
    setSaveError('');
    try {
      const payload: any = {
        soap_subjective:    state.soap_subjective,
        soap_objective:     state.soap_objective,
        soap_assessment:    state.soap_assessment,
        soap_plan:          state.soap_plan,
        primary_icd10_code: state.primary_icd10_code,
        primary_icd10_desc: state.primary_icd10_desc,
        follow_up_date:     state.follow_up_date || undefined,
        referral_to:        state.referral_to    || undefined,
      };
      if (markComplete) payload.status = 'completed';
      await opdApi.updateEncounter(encounter.id, payload);

      // Save prescription items when completing consultation
      if (state.rxItems.length && markComplete) {
        await api.post('/prescriptions', {
          encounter_id:         encounter.id,
          patient_id:           encounter.patient_id,
          items:                state.rxItems,
          general_instructions: state.general_instructions || undefined,
        });
      }

      setDirty(false);
      setSaved(true);
      queryClient.invalidateQueries({ queryKey: ['opd-queue'] });
      return true;
    } catch (err: any) {
      const msg = err?.response?.data?.error ?? err?.message ?? 'Save failed. Please try again.';
      setSaveError(msg);
      return false;
    } finally {
      setSaving(false);
    }
  }, [state, dirty, encounter.id, queryClient]);

  useEffect(() => {
    autoSaveRef.current = setInterval(() => doSave(false), 30_000);
    return () => { if (autoSaveRef.current) clearInterval(autoSaveRef.current); };
  }, [doSave]);

  function addRxItem(drug: any) {
    const item: RxItem = {
      drug_name:  drug.generic_name ?? drug,
      drug_id:    drug.id,
      dose:       drug.strength ?? '',
      frequency:  'BD',
      duration:   '5 days',
    };
    set('rxItems', [...state.rxItems, item]);
  }

  function addManualRxItem() {
    set('rxItems', [...state.rxItems, { drug_name: '', dose: '', frequency: 'BD', duration: '5 days' }]);
  }

  function updateRxItem(i: number, key: keyof RxItem, val: any) {
    const items = [...state.rxItems];
    items[i] = { ...items[i], [key]: val };
    set('rxItems', items);
  }

  function removeRxItem(i: number) {
    set('rxItems', state.rxItems.filter((_, idx) => idx !== i));
  }

  const SOAP_FIELDS: { key: keyof ConsultState; label: string; placeholder: string }[] = [
    { key: 'soap_subjective', label: 'Subjective (S)', placeholder: 'Chief complaint, history of present illness, patient-reported symptoms…' },
    { key: 'soap_objective',  label: 'Objective (O)',  placeholder: 'Examination findings, vitals, investigations…' },
    { key: 'soap_assessment', label: 'Assessment (A)', placeholder: 'Diagnosis / differential diagnosis…' },
    { key: 'soap_plan',       label: 'Plan (P)',       placeholder: 'Treatment plan, investigations ordered, advice given…' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-stretch justify-end">
      <div className="w-full max-w-2xl bg-white flex flex-col shadow-2xl h-full overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-navy">T-{String(encounter.token_number).padStart(2,'0')}</span>
              <span className="text-gray-400 text-sm">·</span>
              <span className="font-semibold text-gray-700">{encounter.patient_name}</span>
            </div>
            <div className="text-xs text-gray-400 mt-0.5">
              {encounter.patient_uid} · {encounter.gender}{encounter.age_years ? ` · ${encounter.age_years}y` : ''}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {saved && !dirty && (
              <span className="text-xs text-green-600 flex items-center gap-1">
                <CheckCircle2 size={12}/> Saved
              </span>
            )}
            {dirty && (
              <span className="text-xs text-yellow-600 flex items-center gap-1">
                <AlertCircle size={12}/> Unsaved
              </span>
            )}
            <button
              onClick={onClose}
              aria-label="Close consultation drawer"
              className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition focus:outline-none focus:ring-2 focus:ring-navy/30"
            >
              <X size={18}/>
            </button>
          </div>
        </div>

        {/* Chief complaint banner */}
        {encounter.chief_complaint && (
          <div className="px-6 py-2.5 bg-blue-50 border-b border-blue-100 flex-shrink-0">
            <span className="text-xs text-blue-500 font-medium">Chief Complaint: </span>
            <span className="text-sm text-blue-700">{encounter.chief_complaint}</span>
          </div>
        )}

        {/* Vitals row (pre-filled by nurse) */}
        {(encounter.vitals_bp_systolic || encounter.vitals_pulse || encounter.vitals_temp_c) && (
          <div className="px-6 py-2.5 bg-gray-50 border-b border-gray-100 flex-shrink-0 flex gap-4 flex-wrap text-xs text-gray-600">
            {encounter.vitals_bp_systolic && (
              <span>BP: <strong>{encounter.vitals_bp_systolic}/{encounter.vitals_bp_diastolic}</strong> mmHg</span>
            )}
            {encounter.vitals_pulse && (
              <span>Pulse: <strong>{encounter.vitals_pulse}</strong> bpm</span>
            )}
            {encounter.vitals_temp_c && (
              <span>Temp: <strong>{encounter.vitals_temp_c}°C</strong></span>
            )}
            {encounter.vitals_spo2 && (
              <span>SpO₂: <strong>{encounter.vitals_spo2}%</strong></span>
            )}
            {encounter.vitals_weight_kg && (
              <span>Wt: <strong>{encounter.vitals_weight_kg} kg</strong></span>
            )}
          </div>
        )}

        {/* Tab nav */}
        <div className="flex gap-0 border-b border-gray-100 flex-shrink-0">
          {[
            { id: 'soap', label: 'SOAP Notes & Diagnosis' },
            { id: 'rx',   label: `Prescription${state.rxItems.length ? ` (${state.rxItems.length})` : ''}` },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id as any)}
              className={clsx(
                'px-5 py-3 text-sm font-medium border-b-2 transition-colors -mb-px',
                activeTab === t.id
                  ? 'border-navy text-navy'
                  : 'border-transparent text-gray-400 hover:text-gray-600'
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {activeTab === 'soap' && (
            <>
              {/* Historical vitals from last completed encounter for clinical reference */}
              {encounter.patient_id && (
                <PreviousVitalsCard
                  patientId={encounter.patient_id}
                  excludeEncounterId={encounter.id}
                />
              )}

              {SOAP_FIELDS.map(f => (
                <div key={f.key}>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                    {f.label}
                  </label>
                  <textarea
                    value={state[f.key] as string}
                    onChange={e => set(f.key, e.target.value)}
                    disabled={!isDoctor}
                    rows={3}
                    placeholder={f.placeholder}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm resize-none focus:outline-none focus:border-navy disabled:bg-gray-50 disabled:text-gray-500"
                  />
                </div>
              ))}

              {/* ICD-10 Diagnosis */}
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                  Primary Diagnosis (ICD-10)
                </label>
                <IcdSearch
                  value={state.primary_icd10_code ? `${state.primary_icd10_code} — ${state.primary_icd10_desc}` : ''}
                  onChange={(code, desc) => {
                    set('primary_icd10_code', code);
                    set('primary_icd10_desc', desc);
                  }}
                />
              </div>

              {/* Follow-up & Referral */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                    Follow-up Date
                  </label>
                  <input
                    type="date"
                    value={state.follow_up_date}
                    onChange={e => set('follow_up_date', e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-navy"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                    Referral To
                  </label>
                  <input
                    type="text"
                    value={state.referral_to}
                    onChange={e => set('referral_to', e.target.value)}
                    placeholder="Specialist / hospital…"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-navy"
                  />
                </div>
              </div>
            </>
          )}

          {activeTab === 'rx' && (
            <>
              {/* Drug search */}
              <DrugSearch onSelect={addRxItem}/>

              {/* Prescription items */}
              <div className="space-y-3">
                {state.rxItems.length === 0 ? (
                  <div className="text-center py-8 text-gray-300">
                    <p className="text-sm">No drugs added yet</p>
                    <p className="text-xs mt-1">Search above or type a drug name manually</p>
                  </div>
                ) : (
                  state.rxItems.map((item, i) => (
                    <div key={i} className="border border-gray-200 rounded-xl p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <input
                          value={item.drug_name}
                          onChange={e => updateRxItem(i, 'drug_name', e.target.value)}
                          placeholder="Drug name"
                          className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm font-medium focus:outline-none focus:border-navy"
                        />
                        <button onClick={() => removeRxItem(i)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition">
                          <Trash2 size={14}/>
                        </button>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="text-xs text-gray-400 mb-1 block">Dose</label>
                          <input
                            value={item.dose}
                            onChange={e => updateRxItem(i, 'dose', e.target.value)}
                            placeholder="e.g. 500mg"
                            className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-navy"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-400 mb-1 block">Frequency</label>
                          <select
                            value={item.frequency}
                            onChange={e => updateRxItem(i, 'frequency', e.target.value)}
                            className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-navy"
                          >
                            {FREQUENCY_OPTIONS.map(f => <option key={f}>{f}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="text-xs text-gray-400 mb-1 block">Duration</label>
                          <select
                            value={item.duration}
                            onChange={e => updateRxItem(i, 'duration', e.target.value)}
                            className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-navy"
                          >
                            {DURATION_OPTIONS.map(d => <option key={d}>{d}</option>)}
                          </select>
                        </div>
                      </div>
                      <input
                        value={item.instructions ?? ''}
                        onChange={e => updateRxItem(i, 'instructions', e.target.value)}
                        placeholder="Special instructions (optional)"
                        className="w-full border border-gray-100 bg-gray-50 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-navy"
                      />
                    </div>
                  ))
                )}

                <button
                  onClick={addManualRxItem}
                  className="w-full py-2.5 border border-dashed border-gray-200 rounded-xl text-sm text-gray-400 hover:text-navy hover:border-navy/30 transition-colors flex items-center justify-center gap-2"
                >
                  <Plus size={14}/> Add drug manually
                </button>
              </div>

              {/* General instructions */}
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                  General Instructions
                </label>
                <textarea
                  value={state.general_instructions}
                  onChange={e => set('general_instructions', e.target.value)}
                  rows={2}
                  placeholder="Drink plenty of water, avoid spicy food, rest for 3 days…"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm resize-none focus:outline-none focus:border-navy"
                />
              </div>
            </>
          )}
        </div>

        {/* Footer actions */}
        <div className="border-t border-gray-100 flex-shrink-0 bg-gray-50">
          {saveError && (
            <div className="flex items-center gap-2 px-6 py-2.5 bg-red-50 border-b border-red-100 text-xs text-red-600">
              <AlertCircle size={13} className="flex-shrink-0"/>
              {saveError}
            </div>
          )}
          <div className="flex items-center justify-between px-6 py-4">
            <button
              onClick={() => doSave(false)}
              disabled={saving || !dirty}
              className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-white disabled:opacity-40 transition"
            >
              <Save size={14}/>
              {saving ? 'Saving…' : 'Save Draft'}
            </button>
            <div className="flex gap-2">
              <button
                onClick={async () => {
                  setSaveError('');
                  try {
                    await doSave(false);
                    await opdApi.updateEncounter(encounter.id, { status: 'no_show' });
                    queryClient.invalidateQueries({ queryKey: ['opd-queue'] });
                    onClose();
                  } catch (err: any) {
                    setSaveError(err?.response?.data?.error ?? err?.message ?? 'Failed to mark no show');
                  }
                }}
                disabled={saving}
                className="px-4 py-2 rounded-xl border border-gray-200 text-sm text-gray-500 hover:bg-white disabled:opacity-40 transition"
              >
                No Show
              </button>
              <button
                onClick={async () => {
                  const ok = await doSave(true);
                  if (ok) onClose();
                }}
                disabled={saving}
                className="px-5 py-2 rounded-xl bg-orange text-gray-900 text-sm font-semibold hover:bg-orange-dark disabled:opacity-50 transition flex items-center gap-2 shadow-sm"
              >
                <CheckCircle2 size={14}/>
                {saving ? 'Saving…' : 'Complete Consultation'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Patient Registration + Walk-in Booking Modal ───────────────────

interface RegForm {
  name: string; gender: string; mobile: string; age_years: string; dob: string;
  blood_group: string; email: string; address_line: string; city: string;
  pin_code: string; emergency_contact_name: string; emergency_contact_mobile: string;
  known_allergies: string; chronic_conditions: string;
  // appointment
  doctor_id: string; chief_complaint: string; appointment_type: string;
}

const BLANK_FORM: RegForm = {
  name: '', gender: 'male', mobile: '', age_years: '', dob: '',
  blood_group: '', email: '', address_line: '', city: '',
  pin_code: '', emergency_contact_name: '', emergency_contact_mobile: '',
  known_allergies: '', chronic_conditions: '',
  doctor_id: '', chief_complaint: '', appointment_type: 'new',
};

function NewPatientModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<RegForm>(BLANK_FORM);
  const [step, setStep] = useState<'search' | 'register' | 'book'>('search');
  const [foundPatient, setFoundPatient] = useState<any>(null);
  const [error, setError] = useState('');

  const f = (k: keyof RegForm, v: string) => setForm(p => ({ ...p, [k]: v }));

  // Debounced patient search — fires 300 ms after the user stops typing
  const {
    query:       searchQ,
    setQuery:    setSearchQ,
    results:     searchResults,
    isSearching: searching,
  } = usePatientSearch(300, 2);

  // Doctors list
  const { data: doctors = [] } = useQuery({
    queryKey: ['doctors-list'],
    queryFn:  () => api.get('/users', { params: { role: 'doctor' } }).then(r => r.data.users ?? r.data),
    staleTime: 60_000,
  });

  function apiError(err: any): string {
    const data = err?.response?.data;
    if (!data) return err?.message ?? 'Request failed';
    // Zod validation error — pick first issue message
    if (data.issues?.length) return data.issues[0].message;
    return data.error ?? data.message ?? 'Request failed';
  }

  const INDIAN_MOBILE = /^[6-9]\d{9}$/;

  const registerMutation = useMutation({
    mutationFn: (payload: any) => api.post('/patients', payload),
    onSuccess: (res, payload) => {
      // API returns { id, patient_uid, duplicateWarnings } — supplement with form data for display
      setFoundPatient({
        id:          res.data.id,
        patient_uid: res.data.patient_uid,
        name:        payload.name,
        gender:      payload.gender,
        age_years:   payload.age_years ?? undefined,
        duplicateWarnings: res.data.duplicateWarnings ?? [],
      });
      setStep('book');
    },
    onError: (err: any) => setError(apiError(err)),
  });

  const bookMutation = useMutation({
    mutationFn: (payload: any) => api.post('/opd/encounters', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opd-queue'] });
      onClose();
    },
    onError: (err: any) => setError(apiError(err)),
  });

  function handleRegister() {
    setError('');
    // Client-side validation
    if (!form.name.trim())  { setError('Patient name is required'); return; }
    if (!INDIAN_MOBILE.test(form.mobile.trim())) {
      setError('Mobile must be a 10-digit Indian number starting with 6–9'); return;
    }
    if (!form.dob && !form.age_years) {
      setError('Please enter date of birth or age'); return;
    }
    const emergMobile = form.emergency_contact_mobile.trim();
    if (emergMobile && !INDIAN_MOBILE.test(emergMobile)) {
      setError('Emergency mobile must be a 10-digit Indian number starting with 6–9'); return;
    }

    const payload: any = {
      name:    form.name.trim(),
      gender:  form.gender,
      mobile:  form.mobile.trim(),
      registration_source: 'walk_in',
    };
    if (form.dob)       payload.dob       = form.dob;
    if (form.age_years) payload.age_years = parseInt(form.age_years);
    if (form.blood_group)         payload.blood_group   = form.blood_group;
    if (form.email.trim())        payload.email         = form.email.trim();
    if (form.address_line.trim()) payload.address_line  = form.address_line.trim();
    if (form.city.trim())         payload.city          = form.city.trim();
    if (form.pin_code.trim())     payload.pin_code      = form.pin_code.trim();
    if (form.emergency_contact_name.trim())
      payload.emergency_contact_name = form.emergency_contact_name.trim();
    if (emergMobile)
      payload.emergency_contact_mobile = emergMobile;
    if (form.known_allergies.trim())
      payload.known_allergies = form.known_allergies.split(',').map((s: string) => s.trim()).filter(Boolean);
    if (form.chronic_conditions.trim())
      payload.chronic_conditions = form.chronic_conditions.split(',').map((s: string) => s.trim()).filter(Boolean);
    payload.consent = { given_by: form.name.trim(), given_via: 'verbal_witnessed' };
    registerMutation.mutate(payload);
  }

  function handleBook() {
    setError('');
    if (!foundPatient) return;
    if (!form.doctor_id) { setError('Please select a doctor'); return; }
    bookMutation.mutate({
      patient_id:      foundPatient.id,
      doctor_id:       form.doctor_id,
      encounter_type:  'opd',
      chief_complaint: form.chief_complaint.trim() || undefined,
      appointment_type: form.appointment_type,
    });
  }

  const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-navy';
  const labelCls = 'text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            <User size={18} className="text-navy"/>
            <h2 className="text-lg font-bold text-navy">
              {step === 'search'   ? 'Patient Walk-in'
             : step === 'register' ? 'New Patient Registration'
             :                       'Book Consultation'}
            </h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 transition">
            <X size={18}/>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {/* ── Step: Search ── */}
          {step === 'search' && (
            <>
              <div>
                <label className={labelCls}>Search existing patient</label>
                {/* Relative wrapper so the dropdown can use absolute positioning
                    and float above the scrollable modal body without being clipped */}
                <div className="relative">
                  <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 focus-within:border-navy">
                    <Search size={14} className="text-gray-400 flex-shrink-0"/>
                    <input
                      autoFocus
                      value={searchQ}
                      onChange={e => setSearchQ(e.target.value)}
                      placeholder="Name or UHID…"
                      className="flex-1 text-sm outline-none"
                    />
                    {searching && <RefreshCw size={12} className="animate-spin text-gray-400"/>}
                  </div>

                  {/* Floating dropdown — z-[200] keeps it above the modal overlay */}
                  {searchResults.length > 0 && (
                    <div className="absolute left-0 right-0 top-full mt-1 z-[200] bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden divide-y divide-gray-50 max-h-52 overflow-y-auto">
                      {searchResults.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          className="w-full text-left px-4 py-3 hover:bg-navy/5 transition flex items-center justify-between gap-3"
                          onMouseDown={e => {
                            // Use onMouseDown so the click fires before the input's onBlur
                            e.preventDefault();
                            setFoundPatient(p);
                            setStep('book');
                          }}
                        >
                          <div>
                            <div className="font-medium text-gray-800 text-sm">{p.name}</div>
                            <div className="text-xs text-gray-400 mt-0.5">
                              {p.patient_uid} · {p.gender}{p.age_years ? ` · ${p.age_years}y` : ''}
                            </div>
                          </div>
                          <span className="text-xs text-navy font-semibold flex-shrink-0">Select →</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* No-results hint */}
                  {searchQ.length >= 2 && !searching && searchResults.length === 0 && (
                    <p className="absolute left-0 right-0 top-full mt-1 z-[200] bg-white border border-gray-100 rounded-xl shadow-sm text-sm text-gray-400 text-center py-3">
                      No matching patient found
                    </p>
                  )}
                </div>
              </div>

              <div className="pt-2 border-t border-gray-100">
                <button
                  onClick={() => setStep('register')}
                  className="w-full py-2.5 border border-dashed border-navy/30 text-navy text-sm rounded-xl hover:bg-navy/5 transition flex items-center justify-center gap-2"
                >
                  <Plus size={14}/> Register new patient
                </button>
              </div>
            </>
          )}

          {/* ── Step: Register ── */}
          {step === 'register' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className={labelCls}>Full Name *</label>
                  <input value={form.name} onChange={e => f('name', e.target.value)}
                    placeholder="Patient's full name" className={inputCls}/>
                </div>
                <div>
                  <label className={labelCls}>Gender *</label>
                  <select value={form.gender} onChange={e => f('gender', e.target.value)} className={inputCls}>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Mobile *</label>
                  <input value={form.mobile} onChange={e => f('mobile', e.target.value)}
                    placeholder="e.g. 9876543210" maxLength={10} className={inputCls}/>
                  <p className="text-xs text-gray-400 mt-1">10 digits, starts with 6–9</p>
                </div>
                <div>
                  <label className={labelCls}>Date of Birth</label>
                  <input type="date" value={form.dob} onChange={e => f('dob', e.target.value)} className={inputCls}/>
                </div>
                <div>
                  <label className={labelCls}>Age (years)</label>
                  <input type="number" value={form.age_years} onChange={e => f('age_years', e.target.value)}
                    placeholder="e.g. 35" min={0} max={150} className={inputCls}/>
                </div>
                <div>
                  <label className={labelCls}>Blood Group</label>
                  <select value={form.blood_group} onChange={e => f('blood_group', e.target.value)} className={inputCls}>
                    <option value="">Unknown</option>
                    {['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(g => <option key={g}>{g}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Email</label>
                  <input value={form.email} onChange={e => f('email', e.target.value)}
                    placeholder="optional" className={inputCls}/>
                </div>
                <div className="col-span-2">
                  <label className={labelCls}>Address</label>
                  <input value={form.address_line} onChange={e => f('address_line', e.target.value)}
                    placeholder="Street / locality" className={inputCls}/>
                </div>
                <div>
                  <label className={labelCls}>City</label>
                  <input value={form.city} onChange={e => f('city', e.target.value)} className={inputCls}/>
                </div>
                <div>
                  <label className={labelCls}>PIN Code</label>
                  <input value={form.pin_code} onChange={e => f('pin_code', e.target.value)}
                    maxLength={6} className={inputCls}/>
                </div>
                <div>
                  <label className={labelCls}>Emergency Contact</label>
                  <input value={form.emergency_contact_name} onChange={e => f('emergency_contact_name', e.target.value)}
                    placeholder="Name" className={inputCls}/>
                </div>
                <div>
                  <label className={labelCls}>Emergency Mobile</label>
                  <input value={form.emergency_contact_mobile} onChange={e => f('emergency_contact_mobile', e.target.value)}
                    placeholder="e.g. 9876543210" maxLength={10} className={inputCls}/>
                  <p className="text-xs text-gray-400 mt-1">Optional — 10 digits, starts with 6–9</p>
                </div>
                <div className="col-span-2">
                  <label className={labelCls}>Known Allergies</label>
                  <input value={form.known_allergies} onChange={e => f('known_allergies', e.target.value)}
                    placeholder="Penicillin, Aspirin… (comma-separated)" className={inputCls}/>
                </div>
                <div className="col-span-2">
                  <label className={labelCls}>Chronic Conditions</label>
                  <input value={form.chronic_conditions} onChange={e => f('chronic_conditions', e.target.value)}
                    placeholder="Diabetes, Hypertension… (comma-separated)" className={inputCls}/>
                </div>
              </div>
              {error && <p className="text-xs text-red-500">{error}</p>}
            </>
          )}

          {/* ── Step: Book ── */}
          {step === 'book' && foundPatient && (
            <>
              <div className="bg-navy/5 border border-navy/10 rounded-xl p-4 flex items-center gap-3">
                <div className="w-9 h-9 bg-navy/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <User size={16} className="text-navy"/>
                </div>
                <div>
                  <div className="font-semibold text-navy text-sm">{foundPatient.name ?? foundPatient.patient_name}</div>
                  <div className="text-xs text-gray-400 mt-0.5">
                    {foundPatient.patient_uid} · {foundPatient.gender}
                    {foundPatient.age_years ? ` · ${foundPatient.age_years}y` : ''}
                  </div>
                </div>
              </div>

              {/* Duplicate warning — shown after fresh registration if similar records exist */}
              {foundPatient.duplicateWarnings?.length > 0 && (
                <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-xl p-3.5">
                  <AlertCircle size={15} className="text-amber-600 flex-shrink-0 mt-0.5"/>
                  <div>
                    <p className="text-xs font-semibold text-amber-800">Possible duplicate patient</p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      {foundPatient.duplicateWarnings.length} existing record(s) with a similar name were found.
                      Verify before proceeding to avoid duplicate registrations.
                    </p>
                  </div>
                </div>
              )}

              <div>
                <label className={labelCls}>Doctor *</label>
                <select value={form.doctor_id} onChange={e => f('doctor_id', e.target.value)} className={inputCls}>
                  <option value="">Select doctor…</option>
                  {(doctors as any[]).map((d: any) => (
                    <option key={d.id} value={d.id}>
                      Dr. {d.name}{d.specialization ? ` — ${d.specialization}` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelCls}>Visit Type</label>
                <select value={form.appointment_type} onChange={e => f('appointment_type', e.target.value)} className={inputCls}>
                  <option value="new">New Patient</option>
                  <option value="follow_up">Follow-up</option>
                  <option value="procedure">Procedure</option>
                </select>
              </div>

              <div>
                <label className={labelCls}>Chief Complaint</label>
                <textarea
                  value={form.chief_complaint}
                  onChange={e => f('chief_complaint', e.target.value)}
                  rows={2}
                  placeholder="Primary reason for visit…"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:border-navy"
                />
              </div>
              {error && <p className="text-xs text-red-500">{error}</p>}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 flex-shrink-0 bg-gray-50">
          <button
            onClick={() => {
              if (step === 'register') setStep('search');
              else if (step === 'book' && !foundPatient?.patient_uid) setStep('register');
              else onClose();
            }}
            className="px-4 py-2 rounded-xl border border-gray-200 text-sm text-gray-500 hover:bg-white transition"
          >
            {step === 'search' ? 'Cancel' : '← Back'}
          </button>

          {step === 'search' && (
            <span className="text-xs text-gray-400">Search or register new patient above</span>
          )}

          {step === 'register' && (
            <button
              onClick={handleRegister}
              disabled={registerMutation.isPending || !form.name.trim() || !form.mobile.trim()}
              className="px-5 py-2 rounded-xl bg-navy text-white text-sm font-medium hover:bg-navy/90 disabled:opacity-50 transition"
            >
              {registerMutation.isPending ? 'Registering…' : 'Register & Continue →'}
            </button>
          )}

          {step === 'book' && (
            <button
              onClick={handleBook}
              disabled={bookMutation.isPending || !form.doctor_id}
              className="px-5 py-2 rounded-xl bg-navy text-white text-sm font-medium hover:bg-navy/90 disabled:opacity-50 transition flex items-center gap-2"
            >
              <Stethoscope size={14}/>
              {bookMutation.isPending ? 'Adding to Queue…' : 'Add to Queue'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Vitals Entry Modal ─────────────────────────────────────────────

function VitalsModal({ encounter, onClose, onSaved }: {
  encounter: any;
  onClose:   () => void;
  onSaved:   () => void;
}) {
  const [form, setForm] = React.useState({
    bp_systolic:  String(encounter.vitals_bp_systolic  ?? ''),
    bp_diastolic: String(encounter.vitals_bp_diastolic ?? ''),
    pulse:        String(encounter.vitals_pulse         ?? ''),
    temp_c:       String(encounter.vitals_temp_c        ?? ''),
    spo2:         String(encounter.vitals_spo2          ?? ''),
    weight_kg:    String(encounter.vitals_weight_kg     ?? ''),
    height_cm:    String(encounter.vitals_height_cm     ?? ''),
    rr:           String(encounter.vitals_rr            ?? ''),
  });
  const [saving, setSaving] = React.useState(false);
  const [error,  setError]  = React.useState('');

  const bmi = React.useMemo(() => {
    const w = parseFloat(form.weight_kg);
    const h = parseFloat(form.height_cm) / 100;
    if (w > 0 && h > 0) return (w / (h * h)).toFixed(1);
    return '';
  }, [form.weight_kg, form.height_cm]);

  function f(key: keyof typeof form, val: string) {
    setForm(s => ({ ...s, [key]: val }));
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      await opdApi.updateVitals(encounter.id, {
        bp_systolic:  form.bp_systolic  ? Number(form.bp_systolic)  : undefined,
        bp_diastolic: form.bp_diastolic ? Number(form.bp_diastolic) : undefined,
        pulse:        form.pulse        ? Number(form.pulse)        : undefined,
        temp_c:       form.temp_c       ? Number(form.temp_c)       : undefined,
        spo2:         form.spo2         ? Number(form.spo2)         : undefined,
        weight_kg:    form.weight_kg    ? Number(form.weight_kg)    : undefined,
        height_cm:    form.height_cm    ? Number(form.height_cm)    : undefined,
        rr:           form.rr           ? Number(form.rr)           : undefined,
      });
      onSaved();
    } catch (e: any) {
      setError(e.response?.data?.error ?? 'Failed to save vitals');
    } finally {
      setSaving(false);
    }
  }

  const inp = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-navy placeholder:text-gray-300';
  const lbl = 'block text-xs font-semibold text-gray-500 mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
              <Activity size={15} className="text-blue-600"/>
            </div>
            <div>
              <h2 className="font-semibold text-navy text-sm">Record Vitals</h2>
              <p className="text-xs text-gray-400">{encounter.patient_name} · Token {encounter.token_number}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition">
            <X size={16}/>
          </button>
        </div>

        {/* Form */}
        <div className="p-6 space-y-4 overflow-y-auto">

          {/* BP */}
          <div>
            <label className={lbl}>Blood Pressure (mmHg)</label>
            <div className="flex items-center gap-2">
              <input
                type="number" value={form.bp_systolic}
                onChange={e => f('bp_systolic', e.target.value)}
                placeholder="Systolic" className={inp}
              />
              <span className="text-gray-400 font-bold">/</span>
              <input
                type="number" value={form.bp_diastolic}
                onChange={e => f('bp_diastolic', e.target.value)}
                placeholder="Diastolic" className={inp}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={lbl}>Pulse (bpm)</label>
              <input type="number" value={form.pulse} onChange={e => f('pulse', e.target.value)} placeholder="e.g. 72" className={inp}/>
            </div>
            <div>
              <label className={lbl}>Temperature (°C)</label>
              <input type="number" step="0.1" value={form.temp_c} onChange={e => f('temp_c', e.target.value)} placeholder="e.g. 37.0" className={inp}/>
            </div>
            <div>
              <label className={lbl}>SpO₂ (%)</label>
              <input type="number" value={form.spo2} onChange={e => f('spo2', e.target.value)} placeholder="e.g. 98" className={inp}/>
            </div>
            <div>
              <label className={lbl}>Resp. Rate (/min)</label>
              <input type="number" value={form.rr} onChange={e => f('rr', e.target.value)} placeholder="e.g. 16" className={inp}/>
            </div>
            <div>
              <label className={lbl}>Weight (kg)</label>
              <input type="number" step="0.1" value={form.weight_kg} onChange={e => f('weight_kg', e.target.value)} placeholder="e.g. 68" className={inp}/>
            </div>
            <div>
              <label className={lbl}>Height (cm)</label>
              <input type="number" step="0.1" value={form.height_cm} onChange={e => f('height_cm', e.target.value)} placeholder="e.g. 170" className={inp}/>
            </div>
          </div>

          {/* Auto BMI */}
          {bmi && (
            <div className="flex items-center gap-2 bg-blue-50 rounded-lg px-4 py-2.5 text-sm">
              <span className="text-blue-400 text-xs font-medium">BMI (auto)</span>
              <span className="font-bold text-blue-700">{bmi}</span>
              <span className="text-blue-400 text-xs">
                {Number(bmi) < 18.5 ? '— Underweight' : Number(bmi) < 25 ? '— Normal' : Number(bmi) < 30 ? '— Overweight' : '— Obese'}
              </span>
            </div>
          )}

          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          <button onClick={onClose} className="px-4 py-2 rounded-xl border border-gray-200 text-sm text-gray-500 hover:bg-white transition">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 px-5 py-2 bg-navy text-white text-sm font-semibold rounded-xl hover:bg-navy/90 disabled:opacity-50 transition">
            {saving ? <RefreshCw size={13} className="animate-spin"/> : <Save size={13}/>}
            Save Vitals
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────

export default function OpdQueuePage() {
  const today       = format(new Date(), 'yyyy-MM-dd');
  const queryClient = useQueryClient();
  const user        = useAuthStore(s => s.user);
  const [selected,      setSelected]      = useState<any>(null);
  const [quickViewId,   setQuickViewId]   = useState<string | null>(null);
  const [queueSearch,   setQueueSearch]   = useState('');
  const [showNewPt,     setShowNewPt]     = useState(false);
  const [printTarget,        setPrintTarget]        = useState<BlankRxData | null>(null);
  const [digitalPrintTarget, setDigitalPrintTarget] = useState<DigitalRxData | null>(null);
  const [digitalPrintLoading,setDigitalPrintLoading]= useState<string | null>(null);
  const [vitalsTarget,  setVitalsTarget]  = useState<any>(null);

  const role = user?.role ?? '';
  const canWriteVitals = ['doctor','nurse','hospital_admin','owner'].includes(role);

  const { data, isLoading, refetch } = useQuery({
    queryKey:        ['opd-queue', today],
    queryFn:         () => opdApi.queue({ date: today }).then(r => r.data),
    refetchInterval: 30_000,
    staleTime:       10_000,
  });

  // WebSocket for real-time queue updates
  useEffect(() => {
    // const wsUrl = (import.meta.env.VITE_WS_URL ?? 'ws://localhost:3001') + '/ws/opd-queue';
    const wsUrl = `${import.meta.env.VITE_WS_URL}/ws/opd-queue`;
    let ws: WebSocket | null = null;
    try {
      ws = new WebSocket(wsUrl);
      ws.onmessage = (msg) => {
        const event = JSON.parse(msg.data);
        if (['token_added','token_called'].includes(event.event)) {
          queryClient.invalidateQueries({ queryKey: ['opd-queue', today] });
        }
      };
    } catch { /* WebSocket not always available */ }
    return () => ws?.close();
  }, [today, queryClient]);

  const { data: branding } = useBranding();

  const stats = data?.stats ?? { total: 0, waiting: 0, in_progress: 0, completed: 0, no_show: 0 };
  const queue = data?.queue ?? [];

  const filteredQueue = useMemo(() => {
    const q = queueSearch.trim().toLowerCase();
    if (!q) return queue;
    return queue.filter((row: any) =>
      (row.patient_name ?? '').toLowerCase().includes(q) ||
      (row.patient_uid  ?? '').toLowerCase().includes(q),
    );
  }, [queue, queueSearch]);

  function buildPrintData(row: any): BlankRxData {
    return {
      hospital: {
        name:       user?.hospitalName ?? 'Hospital',
        address:    branding?.hospital_address    ?? '',
        phone:      branding?.hospital_phone      ?? '',
        licenseNo:  branding?.branding_license_no ?? undefined,
        tagline:    branding?.branding_tagline     ?? undefined,
        logoUrl:    branding?.branding_logo        ?? undefined,
        rxFooter:   branding?.branding_rx_footer   ?? undefined,
        settings:   branding,  // passes full branding → enables PrintHeader custom image
      },
      doctor: {
        name:           row.doctor_name ?? 'Doctor',
        qualifications: row.doctor_reg_number ? `Reg. ${row.doctor_reg_number}` : '',
        specialization: row.specialization ?? undefined,
        regNo:          row.doctor_reg_number ?? undefined,
      },
      patient: {
        name:   row.patient_name ?? '—',
        uid:    row.patient_uid  ?? '—',
        age:    row.age_years    ?? undefined,
        gender: row.gender       ?? 'unknown',
      },
      encounterId: row.id,
      date:        row.encounter_date,
      // Pre-fill from recorded vitals if available; omit to leave handwrite-only lines
      vitals: (row.vitals_bp || row.vitals_pulse || row.vitals_temp_f || row.vitals_spo2) ? {
        bp:     row.vitals_bp     ? String(row.vitals_bp)     : undefined,
        pulse:  row.vitals_pulse  ? String(row.vitals_pulse)  : undefined,
        temp:   row.vitals_temp_f ? String(row.vitals_temp_f) : undefined,
        spo2:   row.vitals_spo2   ? String(row.vitals_spo2)   : undefined,
        weight: row.vitals_weight ? String(row.vitals_weight) : undefined,
        height: row.vitals_height ? String(row.vitals_height) : undefined,
        bmi:    row.vitals_bmi    ? String(row.vitals_bmi)    : undefined,
      } : undefined,
    };
  }

  async function handleDigitalPrint(row: any) {
    setDigitalPrintLoading(row.id);
    try {
      const { data: enc } = await opdApi.getEncounter(row.id);
      const rxData: DigitalRxData = {
        hospital: {
          name:      user?.hospitalName ?? 'Hospital',
          address:   branding?.hospital_address    ?? '',
          phone:     branding?.hospital_phone      ?? '',
          licenseNo: branding?.branding_license_no ?? undefined,
          tagline:   branding?.branding_tagline    ?? undefined,
          logoUrl:   branding?.branding_logo       ?? undefined,
          settings:  branding,
        },
        doctor: {
          name:           enc.doctor?.name           ?? row.doctor_name ?? 'Doctor',
          qualifications: enc.doctor?.reg_number     ? `Reg. ${enc.doctor.reg_number}` : '',
          specialization: enc.doctor?.specialization ?? undefined,
          regNo:          enc.doctor?.reg_number     ?? undefined,
        },
        patient: {
          name:     enc.patient?.name      ?? row.patient_name ?? '—',
          uid:      enc.patient?.uid       ?? row.patient_uid  ?? '—',
          age:      enc.patient?.age_years ?? row.age_years    ?? null,
          gender:   enc.patient?.gender    ?? row.gender       ?? 'unknown',
          allergies: enc.patient?.allergies ?? [],
        },
        encounter: {
          id:                 enc.id,
          encounter_no:       enc.encounter_no       ?? undefined,
          encounter_date:     enc.encounter_date     ?? undefined,
          chief_complaint:    enc.chief_complaint    ?? undefined,
          soap_assessment:    enc.soap_assessment    ?? undefined,
          soap_plan:          enc.soap_plan          ?? undefined,
          primary_icd10_code: enc.primary_icd10_code ?? undefined,
          primary_icd10_desc: enc.primary_icd10_desc ?? undefined,
          follow_up_date:     enc.follow_up_date     ?? undefined,
          referral_to:        enc.referral_to        ?? undefined,
        },
        vitals: enc.vitals ? {
          bp_systolic:  enc.vitals.bp_systolic  ?? null,
          bp_diastolic: enc.vitals.bp_diastolic ?? null,
          pulse:        enc.vitals.pulse        ?? null,
          temp_c:       enc.vitals.temp_c       ?? null,
          spo2:         enc.vitals.spo2         ?? null,
          weight_kg:    enc.vitals.weight_kg    ?? null,
          height_cm:    enc.vitals.height_cm    ?? null,
          rr:           enc.vitals.rr           ?? null,
        } : undefined,
        prescription: enc.prescription ?? null,
      };
      setDigitalPrintTarget(rxData);
    } catch (err) {
      console.error('[INFO] Digital print fetch failed:', err);
    } finally {
      setDigitalPrintLoading(null);
    }
  }

  return (
    <div className="p-6 max-w-screen-xl mx-auto">

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs text-gray-400 uppercase tracking-widest font-medium mb-0.5">
            Outpatient Department
          </p>
          <h1 className="text-navy text-2xl font-semibold">
            OPD Queue — {format(new Date(), 'dd MMM yyyy')}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-400 hover:text-navy hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RefreshCw size={14}/>
            Refresh
          </button>
          <button
            onClick={() => setShowNewPt(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-navy text-white rounded-lg hover:bg-navy/90 transition-colors font-medium"
          >
            <Plus size={16}/>
            New Patient
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total Today"  value={stats.total}     icon={UserCheck}   color="bg-navy/10 text-navy"/>
        <StatCard label="Waiting"      value={stats.waiting}   icon={Clock}       color="bg-blue-100 text-blue-600"/>
        <StatCard label="Completed"    value={stats.completed} icon={CheckCircle2} color="bg-green-100 text-green-600"/>
        <StatCard label="No Show"      value={stats.no_show}   icon={XCircle}     color="bg-red-100 text-red-500"/>
      </div>

      {/* Queue */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-card overflow-hidden">
        <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-navy flex-none">Patient Queue</h2>
          <div className="flex items-center gap-3 ml-auto">
            <ListSearchInput
              placeholder="Search patient…"
              value={queueSearch}
              onChange={setQueueSearch}
              className="w-56"
            />
            <span className="text-xs text-gray-400 flex-none whitespace-nowrap">
              {queueSearch
                ? `${filteredQueue.length} of ${queue.length}`
                : `${queue.length} patient${queue.length !== 1 ? 's' : ''}`}
            </span>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20 text-gray-400 text-sm gap-2">
            <RefreshCw size={16} className="animate-spin"/>
            Loading queue…
          </div>
        ) : queue.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <ClipboardEmptyIcon/>
            <p className="text-sm mt-3">No patients in queue for today</p>
          </div>
        ) : filteredQueue.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 text-gray-400">
            <p className="text-sm">No patients match <strong className="text-navy">{queueSearch}</strong></p>
            <button onClick={() => setQueueSearch('')} className="mt-2 text-xs text-navy hover:underline">
              Clear search
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  {['Token','Patient','Doctor','Slot / Arrival','Vitals','Chief Complaint','Status','Action'].map(h => (
                    <th key={h} className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wide px-5 py-3">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredQueue.map((row: any) => {
                  const statusCfg = STATUS_CONFIG[row.status as keyof typeof STATUS_CONFIG]
                    ?? STATUS_CONFIG.waiting;
                  const isActive  = row.status === 'in_progress';
                  const isDone    = row.status === 'completed' || row.status === 'cancelled' || row.status === 'no_show';

                  return (
                    <tr
                      key={row.id}
                      className={clsx(
                        'transition-colors',
                        isActive && 'bg-orange-50/60',
                        !isDone && 'hover:bg-gray-50/80 cursor-pointer'
                      )}
                      onClick={() => !isDone && setSelected(row)}
                    >
                      <td className="px-5 py-3.5">
                        <div className="flex flex-col gap-0.5">
                          <span className={clsx('text-sm font-bold', isActive ? 'text-orange-600' : 'text-navy')}>
                            T-{String(row.token_number).padStart(2,'0')}
                          </span>
                          {row.appointment_no && (
                            <code className="text-2xs text-gray-400 font-mono">
                              {row.appointment_no}
                            </code>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3.5 max-w-[160px]">
                        <button
                          onClick={e => { e.stopPropagation(); setQuickViewId(row.patient_id); }}
                          className="group text-left w-full"
                          title="Quick patient summary"
                        >
                          <div className="flex items-center gap-1">
                            <span className="text-sm font-medium text-navy truncate group-hover:underline">
                              {row.patient_name}
                            </span>
                            <Eye size={11} className="text-gray-300 group-hover:text-navy flex-none opacity-0 group-hover:opacity-100 transition"/>
                          </div>
                          <div className="text-xs text-gray-400 mt-0.5 truncate">
                            {row.patient_uid} · {row.gender}{row.age_years ? ` · ${row.age_years}y` : ''}
                          </div>
                        </button>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="text-sm text-navy">{row.doctor_name}</div>
                        {row.specialization && <div className="text-xs text-gray-400">{row.specialization}</div>}
                      </td>
                      {/* SLOT / ARRIVAL — show booked slot time for scheduled, arrival time for walk-ins */}
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        {row.slot_start ? (
                          <>
                            <div className="text-sm font-medium text-gray-700">
                              {row.slot_start.slice(0, 5)}
                            </div>
                            <div className="text-xs text-blue-500 font-medium mt-0.5">
                              {row.appt_type === 'follow_up' ? 'Follow-up' : row.appt_type === 'procedure' ? 'Procedure' : 'Scheduled'}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="text-sm font-medium text-gray-700">
                              {row.created_at ? format(new Date(row.created_at), 'hh:mm aa') : '—'}
                            </div>
                            <div className="text-xs text-orange-500 font-medium mt-0.5">Walk-in</div>
                          </>
                        )}
                      </td>
                      <td className="px-5 py-3.5">
                        {row.vitals_bp_systolic ? (
                          <div className="text-xs text-gray-600 space-y-0.5">
                            <div>{row.vitals_bp_systolic}/{row.vitals_bp_diastolic} mmHg</div>
                            {row.vitals_pulse && <div>P: {row.vitals_pulse} bpm</div>}
                            {row.vitals_spo2  && <div>SpO₂: {row.vitals_spo2}%</div>}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 max-w-[180px]">
                        <span className="text-sm text-gray-600 truncate block max-w-[160px]"
                              title={row.chief_complaint ?? undefined}>
                          {row.chief_complaint ?? '—'}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={clsx(
                          'inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full border',
                          statusCfg.color
                        )}>
                          {statusCfg.label}
                        </span>
                      </td>
                      <td className="px-5 py-3.5" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-1.5">
                          {/* Vitals — nurse / doctor / admin enter before consult */}
                          {canWriteVitals && row.status !== 'cancelled' && (
                            <button
                              onClick={() => setVitalsTarget(row)}
                              className={clsx(
                                'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition',
                                row.vitals_bp_systolic
                                  ? 'bg-green-50 text-green-700 hover:bg-green-100'
                                  : 'bg-blue-50 text-blue-700 hover:bg-blue-100',
                              )}
                              title={row.vitals_bp_systolic ? 'Edit vitals' : 'Record vitals'}
                            >
                              <Activity size={12}/>
                              {row.vitals_bp_systolic ? 'Vitals ✓' : 'Vitals'}
                            </button>
                          )}
                          {!isDone && (
                            <button
                              onClick={() => setSelected(row)}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-navy/5 text-navy text-xs font-medium rounded-lg hover:bg-navy/10 transition"
                            >
                              <Stethoscope size={12}/>
                              Consult
                            </button>
                          )}
                          {/* Print Blank Rx — available for all non-cancelled rows */}
                          {row.status !== 'cancelled' && (
                            <button
                              onClick={() => setPrintTarget(buildPrintData(row))}
                              title="Print Blank Prescription"
                              aria-label="Print blank prescription for this patient"
                              className="p-1.5 text-gray-400 hover:text-navy hover:bg-navy/5 rounded-lg transition"
                            >
                              <Printer size={13}/>
                            </button>
                          )}
                          {/* Print Digital Rx — only for completed encounters */}
                          {row.status === 'completed' && (
                            <button
                              onClick={() => handleDigitalPrint(row)}
                              disabled={digitalPrintLoading === row.id}
                              title="Print Digital Prescription"
                              aria-label="Print digital prescription with clinical notes"
                              className="p-1.5 text-orange-500 hover:text-orange-700 hover:bg-orange-50 rounded-lg transition disabled:opacity-40"
                            >
                              {digitalPrintLoading === row.id
                                ? <RefreshCw size={13} className="animate-spin"/>
                                : <FileText size={13}/>
                              }
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Consultation modal */}
      {selected && (
        <ConsultModal
          encounter={selected}
          onClose={() => {
            setSelected(null);
            queryClient.invalidateQueries({ queryKey: ['opd-queue', today] });
          }}
        />
      )}

      {/* Patient quick-view modal */}
      <PatientQuickViewModal
        patientId={quickViewId}
        isOpen={!!quickViewId}
        onClose={() => setQuickViewId(null)}
      />

      {/* New Patient / Walk-in modal */}
      {showNewPt && (
        <NewPatientModal onClose={() => setShowNewPt(false)}/>
      )}

      {/* Blank Rx print — portal to document.body, triggers window.print() */}
      {printTarget && (
        <BlankRxPrint
          data={printTarget}
          onDone={() => setPrintTarget(null)}
        />
      )}

      {/* Digital Rx print — portal to document.body, triggers window.print() */}
      {digitalPrintTarget && (
        <DigitalPrescriptionPrint
          data={digitalPrintTarget}
          onDone={() => setDigitalPrintTarget(null)}
        />
      )}

      {/* Vitals entry modal */}
      {vitalsTarget && (
        <VitalsModal
          encounter={vitalsTarget}
          onClose={() => setVitalsTarget(null)}
          onSaved={() => {
            setVitalsTarget(null);
            queryClient.invalidateQueries({ queryKey: ['opd-queue', today] });
          }}
        />
      )}
    </div>
  );
}

function ClipboardEmptyIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
      <rect x="9" y="3" width="6" height="4" rx="1"/>
    </svg>
  );
}
